import type { ArtistRow } from './leaderboardData';
import { Flame, Trophy, Sparkles, ChevronUp, ChevronDown, Minus, Star } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  rows: ArtistRow[];
  /** When set, only render the top N rows (for TV mode). */
  topN?: number;
  variant?: 'standard' | 'tv';
}

const RANK_ICON: Record<number, { Icon: typeof Trophy; tone: string }> = {
  0: { Icon: Trophy, tone: 'text-gold-bright' },
  1: { Icon: Trophy, tone: 'text-cream' },
  2: { Icon: Trophy, tone: 'text-blood' },
};

export default function LeaderboardTable({ rows, topN, variant = 'standard' }: Props) {
  const slice = topN ? rows.slice(0, topN) : rows;
  if (slice.length === 0) {
    return (
      <p
        className="font-serif text-cream-soft"
        data-testid="leaderboard-empty"
      >
        No rebooks recorded this month yet.
      </p>
    );
  }
  const isTv = variant === 'tv';
  return (
    <ol
      className={clsx('space-y-2', isTv && 'space-y-4')}
      data-testid="leaderboard-list"
    >
      {slice.map((row, idx) => {
        const rank = RANK_ICON[idx];
        const RankIcon = rank?.Icon;
        return (
          <li
            key={row.artistId}
            data-testid={`leaderboard-row-${row.artistId}`}
            className={clsx(
              'panel-dark flex items-center gap-3',
              isTv ? 'p-5' : 'p-3',
              idx === 0 && 'border border-gold-bright/40',
            )}
          >
            <span
              className={clsx(
                'shrink-0 flex flex-col items-center justify-center rounded-full gap-0.5',
                isTv ? 'w-14 h-14' : 'w-10 h-10',
                rank ? rank.tone : 'text-cream-soft',
                idx === 0 ? 'bg-gold/10' : 'bg-ink-deep',
              )}
            >
              {RankIcon ? (
                <RankIcon size={isTv ? 28 : 18} />
              ) : (
                <span className={clsx('font-cinzel', isTv ? 'text-2xl' : 'text-sm')}>
                  {idx + 1}
                </span>
              )}
            </span>
            <MovementIndicator row={row} isTv={isTv} />
            <div className="flex-1 min-w-0">
              <p
                className={clsx(
                  'font-inter text-cream truncate',
                  isTv ? 'text-3xl' : 'text-base',
                )}
              >
                {row.name}
              </p>
              <p
                className={clsx(
                  'font-inter text-cream-soft uppercase tracking-widest',
                  isTv ? 'text-sm mt-1' : 'text-[10px]',
                )}
              >
                {row.totalCompletedThisMonth} sessions · {row.studioId.replace('-', ' ')}
              </p>
              {row.badges.length > 0 ? (
                <div className={clsx('flex flex-wrap gap-1.5', isTv ? 'mt-2' : 'mt-1')}>
                  {row.badges.map((b) => (
                    <span
                      key={b}
                      className={clsx(
                        'inline-flex items-center gap-1 rounded-full border border-gold-bright/40 bg-gold/10 text-gold-bright font-inter uppercase tracking-widest',
                        isTv ? 'text-xs px-2 py-1' : 'text-[9px] px-1.5 py-0.5',
                      )}
                    >
                      <Sparkles size={isTv ? 12 : 9} /> {b}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="text-right shrink-0">
              <p
                data-testid={`leaderboard-rebooks-${row.artistId}`}
                className={clsx(
                  'font-inter text-gold-bright',
                  isTv ? 'text-5xl' : 'text-2xl',
                )}
              >
                {row.rebooksThisMonth}
              </p>
              <p
                className={clsx(
                  'font-inter text-cream-soft uppercase tracking-widest',
                  isTv ? 'text-xs' : 'text-[10px]',
                )}
              >
                rebooks
              </p>
              {row.streak > 0 ? (
                <p
                  data-testid={`leaderboard-streak-${row.artistId}`}
                  className={clsx(
                    'inline-flex items-center gap-1 mt-1 text-blood',
                    isTv ? 'text-base' : 'text-xs',
                  )}
                >
                  <Flame size={isTv ? 14 : 11} /> {row.streak}-streak
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Tiny "moved up / moved down vs 24h ago" chip rendered next to the rank
 * trophy. Shows the position delta, or a NEW badge when the artist wasn't on
 * the board yesterday, or a dash when they're holding station.
 */
function MovementIndicator({ row, isTv }: { row: ArtistRow; isTv: boolean }) {
  const size = isTv ? 12 : 10;
  const textSize = isTv ? 'text-[11px]' : 'text-[9px]';
  if (row.movement === undefined) {
    return (
      <span
        data-testid={`leaderboard-movement-${row.artistId}`}
        className={clsx(
          'inline-flex items-center gap-0.5 font-inter uppercase tracking-widest text-mint',
          textSize,
        )}
        title="New on the board in the last 24h"
      >
        <Star size={size} /> NEW
      </span>
    );
  }
  if (row.movement > 0) {
    return (
      <span
        data-testid={`leaderboard-movement-${row.artistId}`}
        className={clsx(
          'inline-flex items-center gap-0.5 font-inter uppercase tracking-widest text-mint',
          textSize,
        )}
        title={`Moved up ${row.movement} spot${row.movement > 1 ? 's' : ''} in the last 24h`}
      >
        <ChevronUp size={size} />
        {row.movement}
      </span>
    );
  }
  if (row.movement < 0) {
    return (
      <span
        data-testid={`leaderboard-movement-${row.artistId}`}
        className={clsx(
          'inline-flex items-center gap-0.5 font-inter uppercase tracking-widest text-blood',
          textSize,
        )}
        title={`Slipped ${Math.abs(row.movement)} spot${Math.abs(row.movement) > 1 ? 's' : ''} in the last 24h`}
      >
        <ChevronDown size={size} />
        {Math.abs(row.movement)}
      </span>
    );
  }
  return (
    <span
      data-testid={`leaderboard-movement-${row.artistId}`}
      className={clsx(
        'inline-flex items-center gap-0.5 font-inter uppercase tracking-widest text-cream-faint',
        textSize,
      )}
      title="No change in position in the last 24h"
    >
      <Minus size={size} />
    </span>
  );
}
